import { useEffect } from 'react';
import * as turf from '@turf/turf';
import centerlinesGeoJSON from './centerlines.json';

const Centerlines = ({ map }) => {
  useEffect(() => {
    if (!map) return;

    const renderLines = () => {

      const centerlineFeatures = [];
      const tickFeatures = [];

      centerlinesGeoJSON.features.forEach((feature, idx) => {
        const { coordinates } = feature.geometry;
        const { course, extensionLineLength, name } = feature.properties;

        const id = `centerline_${name}_${idx}`;
        const direction = (course - 180 + 360) % 360;

        const start = turf.point(coordinates);
        const startOffset = turf.destination(start, 1, direction, { units: 'nauticalmiles' });
        const end = turf.destination(start, extensionLineLength, direction, { units: 'nauticalmiles' });
        
        centerlineFeatures.push({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [startOffset.geometry.coordinates, end.geometry.coordinates],
          },
          properties: { id },
        });

        for (let dist = 1; dist <= extensionLineLength; dist += 1) {
            const tickCenter = turf.destination(start, dist, direction, { units: 'nauticalmiles' });
            const tickLengthType = feature.properties.tickLength || 1;
          
            let halfTickLength = tickLengthType === 2 ? 0.25 : 0.5;
            if (dist % 5 === 0) halfTickLength *= 2.2;
          
            const name = feature.properties.name || '';
            const showLeft = name.endsWith('R');
            const showRight = name.endsWith('L');
          
            const tickLines = [];
          
            if (showLeft || (!showLeft && !showRight)) {
              const left = turf.destination(tickCenter, halfTickLength, direction - 90, { units: 'nauticalmiles' });
              tickLines.push({
                type: 'Feature',
                geometry: {
                  type: 'LineString',
                  coordinates: [tickCenter.geometry.coordinates, left.geometry.coordinates],
                },
                properties: { id: `${id}_tick_${dist}_left` },
              });
            }
          
            if (showRight || (!showLeft && !showRight)) {
              const right = turf.destination(tickCenter, halfTickLength, direction + 90, { units: 'nauticalmiles' });
              tickLines.push({
                type: 'Feature',
                geometry: {
                  type: 'LineString',
                  coordinates: [tickCenter.geometry.coordinates, right.geometry.coordinates],
                },
                properties: { id: `${id}_tick_${dist}_right` },
              });
            }
          
            tickFeatures.push(...tickLines);
        }  
      });

      if (map.getSource('centerlines')) {
        map.getSource('centerlines').setData({ type: 'FeatureCollection', features: centerlineFeatures });
      } else {
        map.addSource('centerlines', { type: 'geojson', data: { type: 'FeatureCollection', features: centerlineFeatures } });
      }

      if (!map.getLayer('centerlines-layer')) {
        map.addLayer({
          id: 'centerlines-layer',
          type: 'line',
          source: 'centerlines',
          paint: {
            'line-color': 'gray',
            'line-width': 1,
          },
          minzoom: 6,
        }, 'aprons');
      }

      if (map.getSource('centerline-ticks')) {
        map.getSource('centerline-ticks').setData({ type: 'FeatureCollection', features: tickFeatures });
      } else {
        map.addSource('centerline-ticks', { type: 'geojson', data: { type: 'FeatureCollection', features: tickFeatures } });
      }

      if (!map.getLayer('centerline-ticks-layer')) {
        map.addLayer({
          id: 'centerline-ticks-layer',
          type: 'line',
          source: 'centerline-ticks',
          paint: {
            'line-color': 'gray',
            'line-width': 1,
          },
          minzoom: 6,
        }, 'aprons');
      }
    };

    if (map.isStyleLoaded()) {
      renderLines();
    } else {
      map.once('styledata', renderLines);
    }

    return () => {
      if (map.getLayer('centerlines-layer')) map.removeLayer('centerlines-layer');
      if (map.getSource('centerlines')) map.removeSource('centerlines');
      if (map.getLayer('centerline-ticks-layer')) map.removeLayer('centerline-ticks-layer');
      if (map.getSource('centerline-ticks')) map.removeSource('centerline-ticks');
    };
  }, [map]);

  return null;
};

export default Centerlines;